import { useEffect, useState } from 'react'
import { Typography, Table, Space, Modal, Form, Button } from 'antd'
import { PlusOutlined, CheckCircleOutlined, StopOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { getTaxStates, createTaxState, updateState } from '../../actions/taxActions'
import { CustomButton } from '../../components/ui/CustomButton'
import { CustomInput } from '../../components/ui/CustomInput'
import { showToast } from '../../utils/toast'
import Colors from '../../utils/colors'
import Constants from '../../utils/constants'
import { removeUnderScoreAndCapitalize } from '../../helpers/strings'

const { Title } = Typography

const CreateState = () => {
  const dispatch = useDispatch()
  const { loading: stateCreationLoading, isStateCreated, error: stateCreationError } = useSelector((state) => state.createState)
  const { loading: statesLoading, states, resultsPerPage, filteredStatesCount } = useSelector((state) => state.allStates)
  const { loading: stateUpdateLoading, isUpdated } = useSelector((state) => state.state)
  
  const [currentPage, setCurrentPage] = useState(1)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [confirmModalVisible, setConfirmModalVisible] = useState(false)
  const [updatingStateId, setUpdatingStateId] = useState(null)
  const [selectedState, setSelectedState] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => {
    dispatch(getTaxStates({ mode: Constants.MODES.BORDER_TAX, page: currentPage }))
  }, [dispatch])

  useEffect(() => {
    if (isStateCreated) {
      showToast('State created successfully', 'success')
      setCreateModalVisible(false)
      form.resetFields()
      dispatch(getTaxStates({ mode: Constants.MODES.BORDER_TAX, page: 1 }))
    }
    if (stateCreationError) {
      showToast(stateCreationError, 'error')
    }
  }, [isStateCreated, stateCreationError])

  useEffect(() => {
    if (isUpdated) {
      showToast('State updated successfully', 'success')
      setUpdatingStateId(null)
      dispatch(getTaxStates({ mode: Constants.MODES.BORDER_TAX, page: currentPage }))
    }
  }, [isUpdated])

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => (currentPage - 1) * (resultsPerPage || 10) + index + 1,
      width: 50
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => removeUnderScoreAndCapitalize(name),
      width: 200
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span style={{
          backgroundColor: status === Constants.STATUS.ACTIVE ? Colors.success : Colors.red,
          color: Colors.white,
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: '500'
        }}>
          {status?.toUpperCase()}
        </span>
      ),
      width: 100
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <CustomButton 
            size="small"
            onClick={() => showConfirmModal(record)}
            className={record.status === Constants.STATUS.ACTIVE ? 'delete-btn' : 'create-btn'}
            style={{ 
              height: '28px',
              fontSize: '10px',
              width: 'auto'
            }}
            loading={stateUpdateLoading && updatingStateId === record._id}
          >
            {record.status === Constants.STATUS.ACTIVE ? (
              <><StopOutlined /> Deactivate</>
            ) : (
              <><CheckCircleOutlined /> Activate</>
            )}
          </CustomButton>
        </Space>
      ),
      width: 120
    }
  ]

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current)
    dispatch(getTaxStates({ mode: Constants.MODES.BORDER_TAX, page: pagination.current }))
  }

  const handleCreateSubmit = (values) => {
    const stateData = {
      ...values,
      mode: Constants.MODES.BORDER_TAX
    }
    dispatch(createTaxState(stateData))
  }

  const handleCreateCancel = () => {
    setCreateModalVisible(false)
    form.resetFields()
  }

  const showConfirmModal = (state) => {
    setSelectedState(state)
    setConfirmModalVisible(true)
  }

  const handleStatusToggle = () => {
    if (selectedState) {
      setUpdatingStateId(selectedState._id)
      const newStatus = selectedState.status === Constants.STATUS.ACTIVE ? Constants.STATUS.INACTIVE : Constants.STATUS.ACTIVE
      dispatch(updateState(selectedState._id, { status: newStatus }))
      setConfirmModalVisible(false)
      setSelectedState(null)
    }
  }

  const cancelStatusToggle = () => {
    setConfirmModalVisible(false)
    setSelectedState(null)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={4} style={{ color: Colors.primary, margin: 0 }}>
          Border Tax States
        </Title>
        <CustomButton
          type="primary"
          onClick={() => setCreateModalVisible(true)}
          className='create-btn'
          style={{ 
            height: '28px',
            fontSize: '10px',
            width: 'auto'
          }}
        >
          <PlusOutlined /> Add New State
        </CustomButton>
      </div>
      
      <div style={{ width: '100%', overflowX: 'auto', border: `1px solid ${Colors.darkerGray}`, borderRadius: 10 }}>
        <Table
          columns={columns}
          dataSource={states}
          loading={statesLoading}
          rowKey={(record) => record._id}
          pagination={{
            current: currentPage,
            total: filteredStatesCount,
            pageSize: resultsPerPage || 10,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} states`,
            itemRender: (current, type, originalElement) => {
              if (type === 'page') {
                return (
                  <span style={{
                    backgroundColor: current === currentPage ? Colors.primary : 'transparent',
                    color: current === currentPage ? Colors.white : Colors.primary,
                    border: `1px solid ${Colors.primary}`,
                    padding: '4px 8px',
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}>
                    {current}
                  </span>
                )
              }
              return originalElement
            }
          }}
          onChange={handleTableChange}
          scroll={{ x: 600 }}
          size="middle"
        />
      </div>

      <Modal
        title="Add New State"
        open={createModalVisible}
        onOk={() => form.submit()}
        onCancel={handleCreateCancel}
        okText="Create State"
        cancelText="Cancel"
        confirmLoading={stateCreationLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Form.Item
            name="name"
            label="State Name"
            rules={[{ required: true, message: 'Please enter state name' }]}
          >
            <CustomInput 
              placeholder="Enter state name" 
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Confirm Status Change"
        open={confirmModalVisible}
        onOk={handleStatusToggle}
        onCancel={cancelStatusToggle}
        okText={selectedState?.status === Constants.STATUS.ACTIVE ? 'Yes, Deactivate' : 'Yes, Activate'}
        cancelText="Cancel"
        okType={selectedState?.status === Constants.STATUS.ACTIVE ? 'danger' : 'primary'}
        centered
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ExclamationCircleOutlined style={{ color: Colors.warning, fontSize: '22px', marginRight: 12 }} />
          <span>
            Are you sure you want to {selectedState?.status === Constants.STATUS.ACTIVE ? Constants.STATUS.INACTIVE : Constants.STATUS.ACTIVE} the state "{removeUnderScoreAndCapitalize(selectedState?.name)}"?
          </span>
        </div>
      </Modal>
    </div>
  )
}

export default CreateState